import { getPaletteColor } from "./palettes";
import type {
  AnalysisBox,
  AnalysisPoint,
  PaletteId,
  ThermalRange,
  Thermogram,
  ZoneMetrics
} from "./types";

export const drawHeatmap = (
  canvas: HTMLCanvasElement,
  thermogram: Thermogram,
  values: Float32Array,
  palette: PaletteId,
  range: ThermalRange
) => {
  canvas.width = thermogram.width;
  canvas.height = thermogram.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const imageData = ctx.createImageData(thermogram.width, thermogram.height);
  const span = Math.max(0.01, range.max - range.min);

  for (let index = 0; index < values.length; index += 1) {
    const value = values[index];
    const normalized = (value - range.min) / span;
    const [r, g, b] = getPaletteColor(palette, normalized);
    const offset = index * 4;
    imageData.data[offset] = r;
    imageData.data[offset + 1] = g;
    imageData.data[offset + 2] = b;
    imageData.data[offset + 3] = 255;
  }
  ctx.putImageData(imageData, 0, 0);
};

// Renders the palette heatmap to a standalone PNG data URL. Used to give the AI
// vision endpoint an image of the scene when the file has no embedded preview.
export const renderHeatmapDataUrl = (
  thermogram: Thermogram,
  values: Float32Array,
  palette: PaletteId,
  range: ThermalRange
): string | undefined => {
  const canvas = document.createElement("canvas");
  drawHeatmap(canvas, thermogram, values, palette, range);
  try {
    return canvas.toDataURL("image/png");
  } catch {
    return undefined;
  }
};

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new window.Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", () => reject(new Error("Image load failed")));
    image.src = src;
  });

export const exportAnnotatedPng = async (
  thermogram: Thermogram,
  values: Float32Array,
  palette: PaletteId,
  range: ThermalRange,
  boxes: AnalysisBox[],
  points: AnalysisPoint[],
  metrics: ZoneMetrics[],
  preferVisual = true
) => {
  const scale = Math.max(2, Math.min(5, Math.floor(1500 / thermogram.width)));
  const header = 72;
  const footer = 42;
  const canvas = document.createElement("canvas");
  canvas.width = thermogram.width * scale;
  canvas.height = thermogram.height * scale + header + footer;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  ctx.fillStyle = "#171b22";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const heatmap = document.createElement("canvas");
  ctx.imageSmoothingEnabled = true;
  try {
    if (!thermogram.visualUrl || !preferVisual) throw new Error("No embedded visual");
    const image = await loadImage(thermogram.visualUrl);
    ctx.drawImage(image, 0, header, thermogram.width * scale, thermogram.height * scale);
  } catch {
    drawHeatmap(heatmap, thermogram, values, palette, range);
    ctx.drawImage(
      heatmap,
      0,
      header,
      thermogram.width * scale,
      thermogram.height * scale
    );
  }

  const metricById = new Map(metrics.map((metric) => [metric.id, metric]));
  ctx.lineWidth = Math.max(2, scale * 0.9);
  ctx.font = `${Math.max(13, scale * 5)}px Inter, system-ui, sans-serif`;
  ctx.textBaseline = "top";

  for (const box of boxes) {
    const metric = metricById.get(box.id);
    const x = box.x * thermogram.width * scale;
    const y = header + box.y * thermogram.height * scale;
    const w = box.width * thermogram.width * scale;
    const h = box.height * thermogram.height * scale;
    ctx.strokeStyle = metric?.severity === "critical" ? "#ff4f62" : "#23c7d9";
    ctx.fillStyle = "rgba(8, 12, 17, 0.55)";
    ctx.strokeRect(x, y, w, h);
    const label = metric
      ? `${box.label}  Max ${metric.max.toFixed(1)} C  Moy ${metric.avg.toFixed(1)} C`
      : box.label;
    const textWidth = ctx.measureText(label).width + 12;
    ctx.fillRect(x, Math.max(header, y - 24), textWidth, 22);
    ctx.fillStyle = "#ffffff";
    ctx.fillText(label, x + 6, Math.max(header + 2, y - 21));
  }

  for (const point of points) {
    const metric = metricById.get(point.id);
    const x = point.x * thermogram.width * scale;
    const y = header + point.y * thermogram.height * scale;
    ctx.fillStyle = "#fff8d4";
    ctx.strokeStyle = "#111827";
    ctx.beginPath();
    ctx.arc(x, y, Math.max(5, scale * 2.2), 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    if (metric) {
      const label = `${point.label} ${metric.max.toFixed(1)} C`;
      ctx.fillStyle = "rgba(8, 12, 17, 0.62)";
      ctx.fillRect(x + 8, y - 10, ctx.measureText(label).width + 12, 22);
      ctx.fillStyle = "#ffffff";
      ctx.fillText(label, x + 14, y - 8);
    }
  }

  ctx.fillStyle = "#f7f8fa";
  ctx.font = `600 22px Inter, system-ui, sans-serif`;
  ctx.fillText("ThermoQ19", 22, 18);
  ctx.font = `14px Inter, system-ui, sans-serif`;
  ctx.fillStyle = "#cdd5df";
  ctx.fillText(
    `${thermogram.file?.name ?? "thermogramme"} · ${thermogram.decodeMode} · ${range.min.toFixed(1)} C / ${range.max.toFixed(1)} C`,
    22,
    45
  );

  ctx.fillStyle = "#cdd5df";
  ctx.fillText(
    "Export d'aide a l'analyse - validation operateur Q19 requise",
    22,
    canvas.height - 28
  );

  const link = document.createElement("a");
  const safeName = (thermogram.file?.name ?? "thermogramme")
    .replace(/\.[^.]+$/, "")
    .replace(/[^a-z0-9_-]+/gi, "-")
    .toLowerCase();
  link.download = `${safeName || "thermogramme"}-thermoq19.png`;
  link.href = canvas.toDataURL("image/png");
  link.click();
};
